import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ProjectManagementService, Project, ProjectListResponse } from '../../services/project-management.service';
import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../services/common.service';
import { GenericHistoryDialogComponent } from '../../dialogs/generic-history-dialog/generic-history-dialog.component';
import { AnalysisStatusService, AnalysisStatus } from '../../services/analysis-status.service';
import { BulkSearchService } from '../../services/bulk-search.service';
import { TaskStatus } from '../../shared/models/bulk-search.models';
import { Subscription } from 'rxjs';
import { CommonConfirmDialogComponent } from '../../dialogs/common-confirm-dialog/common-confirm-dialog.component';
import { ProjectDialogComponent, ProjectDialogData, ProjectDialogResult } from '../../dialogs/project-dialog/project-dialog.component';
import { ReassignProjectOwnerDialogComponent } from '../../dialogs/reassign-project-owner-dialog/reassign-project-owner-dialog.component';
import { ProjectHistoryService, VersionInfo } from '../../services/project-history.service';

@Component({
    selector: 'app-project-management',
    standalone: true,
    imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatIconModule],
    templateUrl: './project-management.component.html',
    styleUrls: ['./project-management.component.scss']
})
export class ProjectManagementComponent implements OnInit, OnDestroy {
    // Service injections
    projectService = inject(ProjectManagementService);
    authService = inject(AuthService);
    route = inject(ActivatedRoute);
    router = inject(Router);
    commonService = inject(CommonService);
    dialog = inject(MatDialog);
    analysisStatusService = inject(AnalysisStatusService);
    snackBar = inject(MatSnackBar);
    bulkSearchService = inject(BulkSearchService);
    projectHistoryService = inject(ProjectHistoryService);
    
    private statusSubscription?: Subscription;

    projects: Project[] = [];
    totalProjects = 0;
    currentPage = 1;
    pageSize = 10;
    totalPages = 0;

    // Modal states
    showDeleteModal = false;
    showStatusModal = false;

    // Selected project for operations
    selectedProject: Project | null = null;
    /** Version count from the list when Edit was opened; used to decide whether to show confirm dialog (get-by-id does not return projectdata_count). */
    selectedProjectVersionCount = 0;

    statusForm = {
        status: 'active'
    };

    // Available statuses
    availableStatuses = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'archived', label: 'Archived' }
    ];

    // Loading states
    loading = false;
    actionLoading = false; // for Analysis button
    saving = false;
    editFormLoading = false; // for Edit modal while fetching full project

    // Error handling
    errorMessage = '';

    // Route-based properties
    isAdminView = false;
    pageTitle = 'Project Management';
    pageSubtitle = 'Manage universities and projects';

    // Flow-based properties
    isGenericFlow = false;
    
    // Analysis status tracking
    activeAnalysisStatus: AnalysisStatus | null = null;
    activeAnalysisProjectId: string | null = null;
    projectLoadingStates: { [projectId: string]: boolean } = {};
    projectProgressPercentages: { [projectId: string]: number | null } = {};
    cancelling = false;
    stoppingTaskId: string | null = null;
    stoppingProjectId: string | null = null;
    private hasAutoOpenedCreateDialog = false;

    ngOnInit(): void {
        // Determine flow type
        this.isGenericFlow = this.commonService.getCurrentFlow();

        // Determine if this is admin or university view based on route
        this.isAdminView = this.route.snapshot.url[0]?.path === 'admin';

        if (this.isAdminView) {
            this.pageTitle = 'Project Management';
            this.pageSubtitle = this.isGenericFlow ? 'Manage all projects and brands' : 'Manage all universities and projects';
        } else {
            this.pageTitle = 'My Projects';
            this.pageSubtitle = this.isGenericFlow ? 'Manage your brand projects' : 'Manage your university projects';
        }

        // Load country and language options from services
        this.loadProjects();
        
        // Check analysis status and start polling
        this.checkAnalysisStatus();
    }
    
    ngOnDestroy(): void {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
        this.analysisStatusService.stopStatusPolling();
    }
    
    /**
     * Check analysis status and start polling if active
     */
    checkAnalysisStatus(): void {
        this.analysisStatusService.checkActiveAnalysis().subscribe({
            next: (status) => {
                this.activeAnalysisStatus = status;
                this.activeAnalysisProjectId = status.project_id || null;
                
                // Update project loading states
                if (status.has_active_analysis && status.project_id) {
                    this.projectLoadingStates[status.project_id] = true;
                }
                
                // Start polling if active analysis exists and not cancelled
                if (status.has_active_analysis && status.status !== 'cancelled') {
                    this.analysisStatusService.resetCancelledFlag(); // Reset flag for new analysis
                    this.analysisStatusService.startStatusPolling();
                    this.subscribeToStatusUpdates();
                } else if (status.status === 'cancelled') {
                    // If status shows cancelled, mark as cancelled and don't start polling
                    this.analysisStatusService.markAsCancelled();
                }
            },
            error: (error) => {
                console.error('Error checking analysis status:', error);
            }
        });
    }
    
    /**
     * Subscribe to status updates
     */
    subscribeToStatusUpdates(): void {
        let previousStatus: AnalysisStatus | null = null;
        
        this.statusSubscription = this.analysisStatusService.getCurrentAnalysisStatus().subscribe({
            next: (status) => {
                if (status) {
                    this.activeAnalysisStatus = status;
                    this.activeAnalysisProjectId = status.project_id || null;
                    
                    // Check if analysis just completed (was active, now not active)
                    if (previousStatus && previousStatus.has_active_analysis && !status.has_active_analysis && status.status !== 'cancelled') {
                        // Analysis completed - show notification
                        const projectName = this.projects.find(p => p.id === status.project_id)?.name || 'project';
                        this.snackBar.open(
                            `✅ Analysis completed for ${projectName}!`,
                            'View',
                            { 
                                duration: 5000,
                                horizontalPosition: 'right',
                                verticalPosition: 'top'
                            }
                        ).onAction().subscribe(() => {
                            if (status.project_id) {
                                this.router.navigate([`/common-ai-analysis/generic/${status.project_id}`]);
                            }
                        });
                    }
                    
                    // Update project loading states and progress
                    if (status.has_active_analysis && status.project_id && status.status !== 'cancelled') {
                        this.projectLoadingStates[status.project_id] = true;
                        this.projectProgressPercentages[status.project_id] =
                            status.progress_percentage != null ? status.progress_percentage : null;
                    } else {
                        // Clear loading states if no active analysis or cancelled
                        if (status.status === 'cancelled' && status.project_id) {
                            // Clear loading state for cancelled project and show alert
                            this.projectLoadingStates[status.project_id] = false;
                            delete this.projectProgressPercentages[status.project_id];
                            if (previousStatus?.has_active_analysis) {
                                this.snackBar.open('Analysis stopped.', 'Close', {
                                    duration: 5000,
                                    horizontalPosition: 'right',
                                    verticalPosition: 'top'
                                });
                            }
                            if (this.stoppingTaskId && status.task_id === this.stoppingTaskId) {
                                this.cancelling = false;
                                this.stoppingTaskId = null;
                                this.stoppingProjectId = null;
                            }
                        } else {
                            // Clear all loading states if no active analysis
                            this.projectLoadingStates = {};
                            this.projectProgressPercentages = {};
                        }
                        this.activeAnalysisProjectId = null;
                    }
                    
                    // Stop polling if cancelled
                    if (status.status === 'cancelled') {
                        this.analysisStatusService.stopStatusPolling();
                    }
                    
                    previousStatus = { ...status };
                } else {
                    // Check if we had an active analysis that just completed
                    if (previousStatus && previousStatus.has_active_analysis && previousStatus.status !== 'cancelled') {
                        const projectName = this.projects.find(p => p.id === previousStatus?.project_id)?.name || 'project';
                        this.snackBar.open(
                            `✅ Analysis completed for ${projectName}!`,
                            'View',
                            { 
                                duration: 5000,
                                horizontalPosition: 'right',
                                verticalPosition: 'top'
                            }
                        ).onAction().subscribe(() => {
                            if (previousStatus?.project_id) {
                                this.router.navigate([`/common-ai-analysis/generic/${previousStatus.project_id}`]);
                            }
                        });
                    }
                    
                    // No active analysis
                    this.activeAnalysisStatus = null;
                    this.activeAnalysisProjectId = null;
                    this.projectLoadingStates = {};
                    this.projectProgressPercentages = {};
                    previousStatus = null;
                }
            }
        });
    }
    
    /**
     * Check if a project has active analysis
     */
    isProjectAnalyzing(projectId: string): boolean {
        return this.projectLoadingStates[projectId] === true;
    }
    
    /**
     * Check if any analysis is active. Only disable buttons when a task is actually running.
     * Treat completed/failed/cancelled as inactive so buttons are not stuck disabled.
     */
    hasActiveAnalysis(): boolean {
        const s = this.activeAnalysisStatus;
        if (!s?.has_active_analysis) return false;
        if (s.status === 'completed' || s.status === 'failed' || s.status === 'cancelled') return false;
        return true;
    }
    
    /**
     * Get analysis progress percentage for a project
     */
    getAnalysisProgress(projectId: string): number | null {
        const v = this.projectProgressPercentages[projectId];
        if (v === null || v === undefined) {
            return null;
        }
        return Math.round(v);
    }
    
    /**
     * Cancel running analysis
     */
    cancelAnalysis(project: Project): void {
        if (!this.activeAnalysisStatus?.task_id) {
            return;
        }
        
        this.cancelling = true;
        this.stoppingTaskId = this.activeAnalysisStatus.task_id;
        this.stoppingProjectId = project.id;
        this.snackBar.open('Stopping analysis...', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top'
        });
        
        // Cancel the task on backend
        this.bulkSearchService.cancelTask(this.activeAnalysisStatus.task_id).subscribe({
            next: (status: TaskStatus) => {
                console.log('✅ Analysis cancelled:', status);
                // Keep showing "Stopping..." until status feed confirms cancelled.
                // Force a refresh after short delay to pick up cancelled state.
                setTimeout(() => {
                    this.analysisStatusService.refreshStatus();
                }, 2500);
            },
            error: (error: any) => {
                console.error('❌ Error cancelling analysis:', error);
                this.snackBar.open(
                    error?.error?.detail || 'Failed to cancel analysis',
                    'Close',
                    { duration: 5000 }
                );
                this.cancelling = false;
                this.stoppingTaskId = null;
                this.stoppingProjectId = null;
                // Refresh status after delay to sync with server
                setTimeout(() => {
                    this.analysisStatusService.refreshStatus();
                }, 2500);
            }
        });
    }

    isStoppingProject(projectId: string): boolean {
        return this.cancelling && this.stoppingProjectId === projectId;
    }

    loadProjects(): void {
        this.loading = true;
        const skip = (this.currentPage - 1) * this.pageSize;

        this.projectService.getAllProjects(skip, this.pageSize).subscribe({
            next: (response: ProjectListResponse) => {
                this.projects = response.projects;
                this.totalProjects = response.total;
                this.totalPages = Math.ceil(this.totalProjects / this.pageSize);
                this.loading = false;

                // Signup onboarding: first-time generic users with no projects
                // should directly see the create project dialog.
                if (
                    !this.hasAutoOpenedCreateDialog &&
                    !this.isAdminView &&
                    this.isGenericFlow &&
                    this.currentPage === 1 &&
                    this.totalProjects === 0
                ) {
                    this.hasAutoOpenedCreateDialog = true;
                    setTimeout(() => this.openCreateModal(), 0);
                }
            },
            error: (error) => {
                console.error('Error loading projects:', error);
                this.errorMessage = 'Failed to load projects';
                this.loading = false;
            }
        });
    }

    openCreateModal(): void {
        this.clearMessages();
        const dialogRef = this.dialog.open(ProjectDialogComponent, {
            width: '80%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            disableClose: true,
            data: { mode: 'create' } as ProjectDialogData,
        });
        dialogRef.afterClosed().subscribe((result: ProjectDialogResult | undefined) => {
            if (!result?.project) return;
            const project = result.project;
            this.projects.unshift(project);
            this.totalProjects++;
            this.snackBar.open('Project created successfully', 'Close', {
                duration: 3000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
            });
            if (result.createAndAnalyze) {
                this.router.navigate([`/common-ai-analysis/generic/${project.id}`], { queryParams: {}, queryParamsHandling: '' });
            }
        });
    }

    openEditModal(project: Project): void {
        this.clearMessages();
        this.projectService.getProjectById(project.id).subscribe({
            next: (fullProject) => {
                const dialogRef = this.dialog.open(ProjectDialogComponent, {
                    width: '80%',
                    maxWidth: '900px',
                    disableClose: true,
                    data: {
                        mode: 'edit',
                        project: fullProject,
                        projectdataCount: project.projectdata_count ?? 0,
                    } as ProjectDialogData,
                });
                dialogRef.afterClosed().subscribe((result: Project | undefined) => {
                    if (result) {
                        const index = this.projects.findIndex((p) => p.id === result.id);
                        if (index !== -1) this.projects[index] = result;
                        this.snackBar.open('Project updated successfully', 'Close', {
                            duration: 3000,
                            horizontalPosition: 'right',
                            verticalPosition: 'top',
                        });
                    }
                });
            },
            error: (err) => {
                console.error('Error loading project for edit:', err);
                this.errorMessage = 'Failed to load project details';
            },
        });
    }

    openDeleteModal(project: Project): void {
        this.selectedProject = project;
        this.showDeleteModal = true;
        this.clearMessages();
    }

    openStatusModal(project: Project): void {
        this.selectedProject = project;
        this.statusForm = {
            status: project.status
        };
        this.showStatusModal = true;
        this.clearMessages();
    }

    closeModals(): void {
        this.showDeleteModal = false;
        this.showStatusModal = false;
        this.selectedProject = null;
        this.selectedProjectVersionCount = 0;
        this.clearMessages();
    }

    deleteProject(): void {
        if (!this.selectedProject) return;

        this.saving = true;
        this.projectService.deleteProject(this.selectedProject.id).subscribe({
            next: () => {
                this.projects = this.projects.filter(p => p.id !== this.selectedProject!.id);
                this.totalProjects--;
                this.snackBar.open('Project deleted successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'right',
                    verticalPosition: 'top',
                });
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error deleting project:', error);
                this.errorMessage = 'Failed to delete project';
                this.saving = false;
            }
        });
    }

    updateProjectStatus(): void {
        if (!this.selectedProject) return;

        this.saving = true;
        this.projectService.updateProjectStatus(this.selectedProject.id, this.statusForm.status).subscribe({
            next: () => {
                const index = this.projects.findIndex(p => p.id === this.selectedProject!.id);
                if (index !== -1) {
                    this.projects[index].status = this.statusForm.status;
                }
                this.snackBar.open('Project status updated successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'right',
                    verticalPosition: 'top',
                });
                this.saving = false;
                this.closeModals();
            },
            error: (error) => {
                console.error('Error updating project status:', error);
                this.errorMessage = 'Failed to update project status';
                this.saving = false;
            }
        });
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.loadProjects();
    }

    formatDate(dateString: string): string {
        return new Date(dateString).toLocaleDateString();
    }

    getStatusDisplay(status: string): string {
        const statusObj = this.availableStatuses.find(s => s.value === status);
        return statusObj ? statusObj.label : status;
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'active': return 'active';
            case 'inactive': return 'inactive';
            case 'archived': return 'archived';
            default: return '';
        }
    }

    clearMessages(): void {
        this.errorMessage = '';
    }

    // Check if current user can perform admin actions
    canManageProjects(): boolean {
        return this.authService.canAccessProjectManagement();
    }

    // Check if current user is admin
    isAdmin(): boolean {
        return this.authService.isAdmin();
    }

    // Navigate to analysis with project parameter (for education flow)
    analyzeProject(project: Project): void {
        // show global loader while navigating to analysis
        this.actionLoading = true;
        // URL encode the project name to handle special characters and spaces
        const encodedProjectName = encodeURIComponent(project.name);

        // Navigate to the education analysis page with the university query parameter
        this.router.navigate(['/common-ai-analysis/education/university'], {
            queryParams: { university: encodedProjectName }
        });
        // fallback: hide loader after short delay if navigation doesn't replace view immediately
        setTimeout(() => (this.actionLoading = false), 2000);
    }

    // Re-Analysis: Navigate to generic analysis page without projectdataId (new analysis)
    reAnalyzeProject(project: Project): void {
        // Check if there's already an active analysis
        if (this.hasActiveAnalysis()) {
            this.snackBar.open(
                'You already have an analysis in progress. Please wait for it to complete.',
                'Close',
                { duration: 5000 }
            );
            return;
        }

        const isReAnalysis = (project.projectdata_count || 0) > 0;
        if (!isReAnalysis) {
            this.navigateToGenericAnalysis(project.id);
            return;
        }

        this.dialog
            .open(CommonConfirmDialogComponent, {
                width: '450px',
                disableClose: true,
                data: {
                    title: 'Confirm Re-Analysis',
                    description:
                        'This will delete the previous version and create a new project. Do you want to continue?',
                },
            })
            .afterClosed()
            .subscribe((confirmed: boolean) => {
                if (!confirmed) return;
                this.actionLoading = true;
                this.projectService.deleteProjectAnalysisVersions(project.id).subscribe({
                    next: () => {
                        this.navigateToGenericAnalysis(project.id);
                    },
                    error: (error) => {
                        console.error('Error deleting previous analysis versions:', error);
                        this.actionLoading = false;
                        this.snackBar.open(
                            error?.error?.detail || 'Failed to delete previous version. Please try again.',
                            'Close',
                            { duration: 5000 }
                        );
                    },
                });
            });
    }
    

    // Previous-Analysis: Open history dialog
    // openHistoryDialog(project: Project): void {
    //     this.dialog.open(GenericHistoryDialogComponent, {
    //         width: '80%',
    //         maxWidth: '90vw',
    //         disableClose: true,
    //         data: {
    //             projectId: project.id,
    //             projectName: project.name
    //         }
    //     });
    // }

    goToBrandDashboard(project: Project): void {
        if ((project.projectdata_count || 0) < 1) {
            return;
        }
        this.projectHistoryService.getProjectVersions(project.id).subscribe({
            next: (response) => {
                const versions = response?.versions ?? [];
                if (!versions.length) {
                    this.snackBar.open('No analysis data found for this project.', 'Close', {
                        duration: 5000,
                    });
                    return;
                }
                const latest = this.pickLatestProjectDataVersion(versions);
                this.router.navigate([`/common-ai-analysis/generic/${project.id}`], {
                    queryParams: { projectdataId: latest.id },
                    queryParamsHandling: '',
                });
            },
            error: (err) => {
                console.error('Error loading project versions for dashboard:', err);
                this.snackBar.open(
                    err?.error?.detail || 'Failed to load project data. Please try again.',
                    'Close',
                    { duration: 5000 }
                );
            },
        });
    }

    shouldShowBrandDashboardButton(project: Project): boolean {
        return (project.projectdata_count || 0) > 0;
    }

    /** Prefer most recently modified, then created (matches “current” analysis view). */
    private pickLatestProjectDataVersion(versions: VersionInfo[]): VersionInfo {
        return [...versions].sort(
            (a, b) =>
                new Date(b.modified_at || b.created_at).getTime() -
                new Date(a.modified_at || a.created_at).getTime()
        )[0];
    }

    private navigateToGenericAnalysis(projectId: string): void {
        this.actionLoading = true;
        this.router.navigate([`/common-ai-analysis/generic/${projectId}`], {
            queryParams: {},
            queryParamsHandling: '',
        });
        setTimeout(() => (this.actionLoading = false), 2000);
    }

    /** Admin + admin route only: change project created_by (same as reassign_project_owner script). */
    openReassignOwnerDialog(project: Project): void {
        this.dialog
            .open(ReassignProjectOwnerDialogComponent, {
                width: '520px',
                maxWidth: '95vw',
                disableClose: true,
                data: { project },
            })
            .afterClosed()
            .subscribe((reassigned) => {
                if (reassigned) {
                    this.loadProjects();
                }
            });
    }
}
